import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  return (
    <div style={styles.container}>
      <nav style={styles.sidebar}>
        <div style={styles.logo}>
          <h2 style={styles.logoText}>Admin Panel</h2>
          <p style={styles.logoSubtext}>KPCS & KSFM 2025</p>
        </div>

        <div style={styles.navLinks}>
          <Link
            to="/"
            style={{
              ...styles.navLink,
              ...(isActive('/') ? styles.navLinkActive : {}),
            }}
          >
            <span style={styles.navIcon}>📊</span>
            Dashboard
          </Link>

          <Link
            to="/audio-input"
            style={{
              ...styles.navLink,
              ...(isActive('/audio-input') ? styles.navLinkActive : {}),
            }}
          >
            <span style={styles.navIcon}>🎤</span>
            Audio Input
          </Link>

          <Link
            to="/glossary"
            style={{
              ...styles.navLink,
              ...(isActive('/glossary') ? styles.navLinkActive : {}),
            }}
          >
            <span style={styles.navIcon}>📚</span>
            Glossary
          </Link>

          <Link
            to="/candidates"
            style={{
              ...styles.navLink,
              ...(isActive('/candidates') ? styles.navLinkActive : {}),
            }}
          >
            <span style={styles.navIcon}>🔍</span>
            Recognition QA
          </Link>

          <Link
            to="/rooms"
            style={{
              ...styles.navLink,
              ...(isActive('/rooms') ? styles.navLinkActive : {}),
            }}
          >
            <span style={styles.navIcon}>🚪</span>
            Rooms
          </Link>

          <Link
            to="/team"
            style={{
              ...styles.navLink,
              ...(isActive('/team') ? styles.navLinkActive : {}),
            }}
          >
            <span style={styles.navIcon}>👥</span>
            Team
          </Link>

          <Link
            to="/organization"
            style={{
              ...styles.navLink,
              ...(isActive('/organization') ? styles.navLinkActive : {}),
            }}
          >
            <span style={styles.navIcon}>🏢</span>
            Organization
          </Link>

          <Link
            to="/settings"
            style={{
              ...styles.navLink,
              ...(isActive('/settings') ? styles.navLinkActive : {}),
            }}
          >
            <span style={styles.navIcon}>⚙️</span>
            Settings
          </Link>
        </div>

        <div style={styles.userSection}>
          <div style={styles.userInfo}>
            <div style={styles.userAvatar}>
              {(user?.displayName || user?.email || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={styles.userName}>{user?.displayName || user?.email}</div>
              <div style={styles.userRole}>{user?.role}</div>
            </div>
          </div>
          <button onClick={logout} style={styles.logoutButton}>
            Sign Out
          </button>
        </div>
      </nav>

      <main style={styles.main}>
        <div style={styles.content}>{children}</div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f7fafc',
  },
  sidebar: {
    width: '280px',
    backgroundColor: '#1a202c',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid #2d3748',
  },
  logo: {
    padding: '24px',
    borderBottom: '1px solid #2d3748',
  },
  logoText: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'white',
    margin: '0 0 4px 0',
  },
  logoSubtext: {
    fontSize: '12px',
    color: '#a0aec0',
    margin: 0,
  },
  navLinks: {
    flex: 1,
    padding: '24px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    color: '#e2e8f0',
    textDecoration: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  navLinkActive: {
    backgroundColor: '#667eea',
    color: 'white',
  },
  navIcon: {
    fontSize: '18px',
  },
  userSection: {
    padding: '20px',
    borderTop: '1px solid #2d3748',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#667eea',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  userName: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
  },
  userRole: {
    fontSize: '12px',
    color: '#a0aec0',
    textTransform: 'capitalize',
  },
  logoutButton: {
    width: '100%',
    padding: '8px',
    backgroundColor: 'transparent',
    border: '1px solid #4a5568',
    borderRadius: '6px',
    color: '#e2e8f0',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  main: {
    flex: 1,
    overflowY: 'auto',
  },
  content: {
    minHeight: '100%',
  },
};
